import { Adresse } from "../types/models";
import { CourseForm } from "../types/forms/course-form";

/**
 * Helpers partagés par le formulaire de course et son affichage en lecture
 * seule : les deux décrivent la même liste d'arrêts et doivent les nommer,
 * les iconifier et les formater de la même façon.
 */

export const createEmptyArret = (
  ordre: number,
): CourseForm["arrets"][number] => ({
  ordre,
  type: 0,
  adresse: {
    rue: "",
    numero: "",
    boite: "",
    codePostal: "",
    ville: "",
    pays: "",
    isNumDetected: false,
    nom: "",
    placeId: "",
  },
  companyTransport: "",
  numeroTransport: "",
});

export const formatAdresse = (adresse: Adresse) => {
  const postale =
    `${adresse.codePostal} ${adresse.ville} (${adresse.pays})`.trim();
  // Lieu nommé (POI, favori) → le nom prime, avec l'adresse postale en complément.
  if (adresse.nom) return `${adresse.nom} — ${postale}`;
  const numero = `${adresse.numero}${adresse.boite ? ` / ${adresse.boite}` : ""}`;

  return `${adresse.rue} ${numero}, ${postale}`;
};

export const isStart = (ordre: number) => ordre === 0;
export const isEnd = (ordre: number, total: number) => ordre === total - 1;
export const isEscale = (ordre: number, total: number) =>
  !isStart(ordre) && !isEnd(ordre, total);

/**
 * Libellés traduits pour {@link getLabelAndIconFromOrdre}. Fonction utilitaire
 * appelée hors du rendu React (pas de hook possible ici) : les traductions
 * sont donc résolues par l'appelant via `useTranslations` et passées en argument.
 */
export type StopLabels = {
  start: string;
  end: string;
  stopover: (ordre: number) => string;
};

export const getLabelAndIconFromOrdre = (
  ordre: number,
  total: number,
  labels: StopLabels,
) => {
  if (isStart(ordre)) return { labelOrdre: labels.start, iconOrdre: "trip_origin" };
  if (isEnd(ordre, total)) return { labelOrdre: labels.end, iconOrdre: "flag" };

  return { labelOrdre: labels.stopover(ordre), iconOrdre: "pin_drop" };
};

/**
 * Le strict nécessaire pour nommer une adresse à l'écran. Volontairement plus
 * lâche qu'{@link Adresse} : les formulaires qui n'ont pas besoin de tous ses
 * champs (tarifs fixes) les affichent avec les mêmes helpers.
 */
type AdresseAffichable = Partial<
  Pick<
    Adresse,
    "rue" | "numero" | "boite" | "codePostal" | "ville" | "pays" | "nom"
  >
>;

/** Une adresse compte comme renseignée dès qu'on a une rue ou une ville :
 *  les suggestions n'ont pas toujours de numéro, de code postal, etc. */
export const hasFilledAddress = (adresse?: AdresseAffichable | null) =>
  !!(adresse && (adresse.rue || adresse.ville));

/**
 * Résumé court affiché sur le bouton de recherche d'adresse. Les parties
 * manquantes sont ignorées pour rester lisible.
 */
export const formatAddressSummary = (adresse?: AdresseAffichable | null) => {
  if (!hasFilledAddress(adresse) || !adresse) return null;

  return [
    `${[adresse.rue, adresse.numero].filter(Boolean).join(" ")}${
      adresse.boite ? ` / ${adresse.boite}` : ""
    }`,
    [adresse.codePostal, adresse.ville].filter(Boolean).join(" "),
  ]
    .filter(Boolean)
    .join(", ");
};

/**
 * Adresse telle que la connaissent les formulaires : {@link AdresseAffichable}
 * plus les deux marqueurs qui disent si le point est réellement localisable.
 */
type AdresseRoutable = AdresseAffichable & {
  isNumDetected?: boolean;
  placeId?: string;
};

/**
 * Une adresse est routable dès qu'elle désigne un point que le backend peut
 * géocoder : une suggestion Google (`placeId`), ou une rue/ville dont le numéro
 * est connu (saisi ou détecté).
 */
export const isRoutableAdresse = (adresse?: AdresseRoutable | null) => {
  if (!adresse) return false;
  if (adresse.placeId?.trim()) return true;

  return (
    hasFilledAddress(adresse) &&
    !!(adresse.isNumDetected || adresse.numero?.trim())
  );
};

/**
 * Un trajet n'est exploitable — carte, itinéraire, estimation de prix — qu'à
 * partir de deux arrêts tous routables. Les formulaires démarrent avec des
 * arrêts vides : sans ce garde-fou, le backend est interrogé avec des adresses
 * vides qu'il ne peut de toute façon pas géocoder.
 */
export const isRoutableTrip = (
  stops?: { adresse?: AdresseRoutable | null }[] | null,
) =>
  !!stops &&
  stops.length >= 2 &&
  stops.every((stop) => isRoutableAdresse(stop.adresse));
